import { Lock, Package } from 'lucide-react'
import { Button } from '@cannect/new-components/atoms'
import { TRecipeMaskedPatient, TRecipeMaskedPrescriber } from '@cannect/services/resources/recipeAccess'
import { formatRecipeDateTime } from '../formatRecipeDate'
import { useRecipeBrandColors } from '../useRecipeBrandColors'

type RecipeAccessScreenProps = {
  patient: TRecipeMaskedPatient
  prescriber: TRecipeMaskedPrescriber | null
  token: string
  emission: string
  onRequestAccess: () => void
  onRequestProducts: () => void
  onRequestHelp: () => void
}

// D1/B2 do protótipo (`Desktop-B-receita.dc.html`, `B-Receita.dc.html`).
// Nome do prescritor no cartão escuro (decisão 53, contrato provisório — o
// Lince ainda está revisando o backend): `GET /baserecipe/:token` agora
// traz `prescriber.name` JÁ mascarado, aditivo, antes do código. `null` só
// omite a linha, sem texto "null" e sem cálculo/máscara no cliente.
export const RecipeAccessScreen = ({
  patient,
  prescriber,
  token,
  emission,
  onRequestAccess,
  onRequestProducts,
  onRequestHelp
}: RecipeAccessScreenProps) => {
  const colors = useRecipeBrandColors()

  return (
    <div className="mx-auto flex w-full flex-col gap-5 lg:max-w-[840px]">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold leading-tight text-gray-950 lg:text-[30px]">
          Olá. Sua receita está pronta.
        </h1>
        <Button unstyled onClick={onRequestHelp} className={`shrink-0 text-sm font-semibold ${colors.link}`}>
          Precisa de ajuda?
        </Button>
      </div>

      <div
        className={`flex flex-col items-start gap-4 rounded-[20px] p-5 text-white lg:flex-row lg:items-center lg:gap-[18px] lg:p-6 ${colors.dark}`}>
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 lg:h-[52px] lg:w-[52px]">
          <Package size={22} className="text-white" />
        </div>
        <div className="flex-grow">
          <p className="text-xs text-white/75">
            Receita de {formatRecipeDateTime(emission)} · código {token}
          </p>
          {prescriber && <p className="mt-0.5 text-sm font-semibold">Dr(a). {prescriber.name}</p>}
          <p className="mt-0.5 text-sm text-white/75">Para {patient.name}</p>
        </div>
        <Button
          onClick={onRequestAccess}
          icon={<Lock size={16} />}
          iconPlacement="left"
          className={`h-11 w-full rounded-[110px] bg-white text-sm hover:bg-white/90 lg:w-auto ${colors.darkText}`}>
          Ver receita
        </Button>
      </div>

      <div
        className={`flex flex-col items-start gap-4 rounded-[20px] p-[18px] lg:flex-row lg:items-center lg:gap-[18px] lg:p-6 ${colors.softBg}`}>
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white lg:h-[52px] lg:w-[52px]">
          <Package size={20} className={colors.softText} />
        </div>
        <div className="flex-grow">
          <h2 className="text-base font-semibold text-gray-950 lg:text-[19px]">Facilite o início do seu tratamento</h2>
          <p className="mt-1 text-[13px] text-gray-700 lg:text-sm">
            Encontre os produtos da sua receita e receba em casa.
          </p>
        </div>
        <Button
          onClick={onRequestProducts}
          className={`h-12 w-full rounded-[110px] text-base text-white lg:w-auto ${colors.cta}`}>
          Ver produtos
        </Button>
      </div>
    </div>
  )
}
